import I18n from 'I18n';
import { JA } from 'pricing-pages-lib/constants/Languages';
import { ENTERPRISE, PROFESSIONAL } from 'self-service-api/constants/ProductLevels';
import { CMS, MARKETING, OPERATIONS, SALES, SERVICE } from 'self-service-api/constants/ProductLines';
import { ADS_LIMIT_INCREASE, BRAND_DOMAIN, CALLING_LIMIT_INCREASE, CUSTOM_SSL, DEDICATED_IP, DOMAINS_LIMIT_INCREASE, LISTS_LIMIT_INCREASE, PHONE_LIMIT_INCREASE, REPORTING_LIMIT_INCREASE, SANDBOX_LIMIT_INCREASE, TEAMS_LIMIT_INCREASE, TRANSACTIONAL_EMAIL, TRANSCRIPTION_LIMIT_INCREASE, WHATSAPP_LIMIT_INCREASE, VIDEO_LIMIT_INCREASE, MARKETING_SMS_ADDON, MARKETING_SMS_LIMIT_INCREASE, WORKFLOWS_LIMIT_INCREASE, USERS_LIMIT_INCREASE, CUSTOM_OBJECTS_LIMIT_INCREASE } from 'self-service-api/constants/ApiNames';
import { productLevelInConfigsSelector } from '../../selectors/ProductConfigsSelectors';
import { highestProductLevelFromProductInventorySelector } from '../../selectors/ProductInventorySelectors';
import { isUngatedForSeatsSelector } from '../../selectors/GatingSelectors'; // helper function that checks all Product Lines and returns false for isRestricted
// if any purchased or selected option matches the passed in allowedProductLevels

const isProductLevelInAnyProductLines = (state, {
  productConfigurations,
  allowedProductLevels
}) => {
  const productLevelsOwned = [highestProductLevelFromProductInventorySelector(state, {
    productLine: SERVICE
  }), highestProductLevelFromProductInventorySelector(state, {
    productLine: MARKETING
  }), highestProductLevelFromProductInventorySelector(state, {
    productLine: CMS
  }), highestProductLevelFromProductInventorySelector(state, {
    productLine: SALES
  }), highestProductLevelFromProductInventorySelector(state, {
    productLine: OPERATIONS
  })];
  const marketingProductLevelSelected = productLevelInConfigsSelector(state, {
    productConfigurations,
    productLine: MARKETING
  });
  const serviceProductLevelSelected = productLevelInConfigsSelector(state, {
    productConfigurations,
    productLine: SERVICE
  });
  const cmsProductLevelSelected = productLevelInConfigsSelector(state, {
    productConfigurations,
    productLine: CMS
  });
  const salesProductLevelSelected = productLevelInConfigsSelector(state, {
    productConfigurations,
    productLine: SALES
  });
  const operationsProductLevelSelected = productLevelInConfigsSelector(state, {
    productConfigurations,
    productLine: OPERATIONS
  });
  const productLevelsSelected = [marketingProductLevelSelected, salesProductLevelSelected, serviceProductLevelSelected, cmsProductLevelSelected, operationsProductLevelSelected];

  if (productLevelsOwned.some(productLevel => productLevel && allowedProductLevels.includes(productLevel))) {
    return false;
  }

  return !productLevelsSelected.some(productLevel => productLevel && allowedProductLevels.includes(productLevel));
};
/**
 * Helper function that allows us to be specific about restricting the addon to
 * a specific product line(s) and specific product level(s)
 *
 * E.g. To restrict Add-on to only Sales Ent and Service Pro + Ent
 *
 * ```
 * isRestrictedProductLinesAndLevels(state, {
 *   productConfigurations,
 *   allowedProductLinesAndLevels: {
 *     [SALES]: [ENTERPRISE],
 *     [SERVICE]: [PROFESSIONAL, ENTERPRISE],
 *   }
 * })
 * ```
 *
 * @param {SubState<'productData'>} state
 * @param {{
 *  productConfigurations: ProductConfiguration[],
 *  allowedProductLinesAndLevels: Record<ProductLine, ProductLevel[]>
 * }} argObj
 * @returns {boolean}
 */


const isRestrictedProductLinesAndLevels = (state, {
  productConfigurations,
  allowedProductLinesAndLevels
}) => {
  for (const [productLine, productLevels] of Object.entries(allowedProductLinesAndLevels)) {
    const productLevelOwned = highestProductLevelFromProductInventorySelector(state, {
      productLine: productLine //  Type of key in Object.entries is always a string so need to recast it here

    });
    const productLevelSelected = productLevelInConfigsSelector(state, {
      productConfigurations,
      productLine: productLine
    });

    for (const productLevel of productLevels) {
      if (productLevelOwned === productLevel || productLevelSelected === productLevel) return false;
    }
  }

  return true;
}; // Versioned entries for addon restrictions.
// fromVersion and toVersion are inclusive
// toVersion is optional (for indicating no upper limit)


const AddOnsRestrictionMap = {
  [BRAND_DOMAIN]: [{
    fromVersion: 1,
    isRestricted: (state, {
      productConfigurations
    }) => {
      const marketingProductLevelOwned = highestProductLevelFromProductInventorySelector(state, {
        productLine: MARKETING
      });
      const marketingProductLevelSelected = productLevelInConfigsSelector(state, {
        productConfigurations,
        productLine: MARKETING
      });
      if (marketingProductLevelOwned === ENTERPRISE || marketingProductLevelSelected === ENTERPRISE) return false;
      return true;
    }
  }],
  [DOMAINS_LIMIT_INCREASE]: [{
    fromVersion: 1,
    isRestricted: (state, {
      productConfigurations
    }) => isRestrictedProductLinesAndLevels(state, {
      productConfigurations,
      allowedProductLinesAndLevels: {
        [CMS]: [ENTERPRISE],
        [MARKETING]: [ENTERPRISE],
        [SERVICE]: [ENTERPRISE]
      }
    })
  }],
  [DEDICATED_IP]: [{
    fromVersion: 1,
    toVersion: 1,
    isRestricted: (state, {
      productConfigurations
    }) => {
      const marketingProductLevelOwned = highestProductLevelFromProductInventorySelector(state, {
        productLine: MARKETING
      });
      const marketingProductLevelSelected = productLevelInConfigsSelector(state, {
        productConfigurations,
        productLine: MARKETING
      });

      if (marketingProductLevelOwned === PROFESSIONAL || marketingProductLevelOwned === ENTERPRISE || marketingProductLevelSelected === PROFESSIONAL || marketingProductLevelSelected === ENTERPRISE) {
        return false;
      }

      return true;
    }
  }, {
    fromVersion: 2,
    isRestricted: (state, {
      productConfigurations
    }) => {
      const marketingProductLevelOwned = highestProductLevelFromProductInventorySelector(state, {
        productLine: MARKETING
      });
      const marketingProductLevelSelected = productLevelInConfigsSelector(state, {
        productConfigurations,
        productLine: MARKETING
      });

      if (marketingProductLevelOwned === PROFESSIONAL || marketingProductLevelOwned === ENTERPRISE || marketingProductLevelSelected === PROFESSIONAL || marketingProductLevelSelected === ENTERPRISE) {
        return false;
      }

      return true;
    }
  }],
  [TRANSACTIONAL_EMAIL]: [{
    fromVersion: 1,
    toVersion: 1,
    isRestricted: (state, {
      productConfigurations
    }) => {
      const marketingProductLevelOwned = highestProductLevelFromProductInventorySelector(state, {
        productLine: MARKETING
      });
      const marketingProductLevelSelected = productLevelInConfigsSelector(state, {
        productConfigurations,
        productLine: MARKETING
      });
      const validMarketingPlan = marketingProductLevelOwned === PROFESSIONAL || marketingProductLevelOwned === ENTERPRISE || marketingProductLevelSelected === PROFESSIONAL || marketingProductLevelSelected === ENTERPRISE;
      if (validMarketingPlan) return false;
      return true;
    }
  }, {
    fromVersion: 2,
    isRestricted: (state, {
      productConfigurations
    }) => {
      const marketingProductLevelOwned = highestProductLevelFromProductInventorySelector(state, {
        productLine: MARKETING
      });
      const marketingProductLevelSelected = productLevelInConfigsSelector(state, {
        productConfigurations,
        productLine: MARKETING
      });
      const validMarketingPlan = marketingProductLevelOwned === PROFESSIONAL || marketingProductLevelOwned === ENTERPRISE || marketingProductLevelSelected === PROFESSIONAL || marketingProductLevelSelected === ENTERPRISE;
      if (validMarketingPlan) return false;
      return true;
    }
  }],
  [CUSTOM_SSL]: [{
    fromVersion: 1,
    isRestricted: (state, {
      productConfigurations
    }) => {
      const productLevelsOwned = [highestProductLevelFromProductInventorySelector(state, {
        productLine: SERVICE
      }), highestProductLevelFromProductInventorySelector(state, {
        productLine: MARKETING
      }), highestProductLevelFromProductInventorySelector(state, {
        productLine: CMS
      })];
      const marketingProductLevelSelected = productLevelInConfigsSelector(state, {
        productConfigurations,
        productLine: MARKETING
      });
      const serviceProductLevelSelected = productLevelInConfigsSelector(state, {
        productConfigurations,
        productLine: SERVICE
      });
      const cmsProductLevelSelected = productLevelInConfigsSelector(state, {
        productConfigurations,
        productLine: CMS
      });

      if (productLevelsOwned.some(productLevel => productLevel === PROFESSIONAL || productLevel === ENTERPRISE)) {
        return false;
      }

      const allowedProductLevels = [PROFESSIONAL, ENTERPRISE];
      return ![serviceProductLevelSelected, marketingProductLevelSelected, cmsProductLevelSelected].some(productLevel => productLevel && allowedProductLevels.includes(productLevel));
    }
  }],
  [ADS_LIMIT_INCREASE]: [{
    fromVersion: 2,
    isRestricted: (state, {
      productConfigurations
    }) => {
      const marketingProductLevelOwned = highestProductLevelFromProductInventorySelector(state, {
        productLine: MARKETING
      });
      const marketingProductLevelSelected = productLevelInConfigsSelector(state, {
        productConfigurations,
        productLine: MARKETING
      });

      if (marketingProductLevelOwned === PROFESSIONAL || marketingProductLevelOwned === ENTERPRISE || marketingProductLevelSelected === PROFESSIONAL || marketingProductLevelSelected === ENTERPRISE) {
        return false;
      }

      return true;
    }
  }],
  [REPORTING_LIMIT_INCREASE]: [{
    fromVersion: 4,
    isRestricted: (state, {
      productConfigurations
    }) => isProductLevelInAnyProductLines(state, {
      productConfigurations,
      allowedProductLevels: [PROFESSIONAL, ENTERPRISE]
    })
  }],
  [TEAMS_LIMIT_INCREASE]: [{
    fromVersion: 1,
    isRestricted: (state, {
      productConfigurations
    }) => isProductLevelInAnyProductLines(state, {
      productConfigurations,
      allowedProductLevels: [PROFESSIONAL, ENTERPRISE]
    })
  }],
  [TRANSCRIPTION_LIMIT_INCREASE]: [{
    fromVersion: 1,
    isRestricted: (state, {
      productConfigurations
    }) => {
      const salesProductLevelOwned = highestProductLevelFromProductInventorySelector(state, {
        productLine: SALES
      });
      const serviceProductLevelOwned = highestProductLevelFromProductInventorySelector(state, {
        productLine: SERVICE
      });
      const salesProductLevelSelected = productLevelInConfigsSelector(state, {
        productConfigurations,
        productLine: SALES
      });
      const serviceProductLevelSelected = productLevelInConfigsSelector(state, {
        productConfigurations,
        productLine: SERVICE
      });

      if (salesProductLevelOwned === ENTERPRISE || salesProductLevelSelected === ENTERPRISE || serviceProductLevelOwned === ENTERPRISE || serviceProductLevelSelected === ENTERPRISE) {
        return false;
      }

      return true;
    }
  }],
  [LISTS_LIMIT_INCREASE]: [{
    fromVersion: 1,
    isRestricted: (state, {
      productConfigurations
    }) => isProductLevelInAnyProductLines(state, {
      productConfigurations,
      allowedProductLevels: [PROFESSIONAL, ENTERPRISE]
    })
  }],
  [SANDBOX_LIMIT_INCREASE]: [{
    fromVersion: 1,
    isRestricted: (state, {
      productConfigurations
    }) => isProductLevelInAnyProductLines(state, {
      productConfigurations,
      allowedProductLevels: [ENTERPRISE]
    })
  }],
  [CALLING_LIMIT_INCREASE]: [{
    fromVersion: 1,
    isRestricted: (state, {
      productConfigurations
    }) => isRestrictedProductLinesAndLevels(state, {
      productConfigurations,
      allowedProductLinesAndLevels: {
        [SALES]: [PROFESSIONAL, ENTERPRISE],
        [SERVICE]: [PROFESSIONAL, ENTERPRISE]
      }
    }) || I18n.lang === JA
  }],
  [PHONE_LIMIT_INCREASE]: [{
    fromVersion: 1,
    isRestricted: (state, {
      productConfigurations
    }) => isRestrictedProductLinesAndLevels(state, {
      productConfigurations,
      allowedProductLinesAndLevels: {
        [SALES]: [PROFESSIONAL, ENTERPRISE],
        [SERVICE]: [PROFESSIONAL, ENTERPRISE]
      }
    }) || I18n === JA
  }],
  [WHATSAPP_LIMIT_INCREASE]: [{
    fromVersion: 1,
    isRestricted: (state, {
      productConfigurations
    }) => isRestrictedProductLinesAndLevels(state, {
      productConfigurations,
      allowedProductLinesAndLevels: {
        [MARKETING]: [PROFESSIONAL, ENTERPRISE],
        [SERVICE]: [PROFESSIONAL, ENTERPRISE]
      }
    })
  }],
  [VIDEO_LIMIT_INCREASE]: [{
    fromVersion: 1,
    isRestricted: (state, {
      productConfigurations
    }) => isRestrictedProductLinesAndLevels(state, {
      productConfigurations,
      allowedProductLinesAndLevels: {
        [MARKETING]: [PROFESSIONAL, ENTERPRISE],
        [SERVICE]: [PROFESSIONAL, ENTERPRISE],
        [CMS]: [PROFESSIONAL, ENTERPRISE]
      }
    })
  }],
  [MARKETING_SMS_ADDON]: [{
    fromVersion: 1,
    isRestricted: (state, {
      productConfigurations
    }) => isRestrictedProductLinesAndLevels(state, {
      productConfigurations,
      allowedProductLinesAndLevels: {
        [MARKETING]: [PROFESSIONAL, ENTERPRISE]
      }
    })
  }],
  [MARKETING_SMS_LIMIT_INCREASE]: [{
    fromVersion: 1,
    isRestricted: (state, {
      productConfigurations
    }) => isRestrictedProductLinesAndLevels(state, {
      productConfigurations,
      allowedProductLinesAndLevels: {
        [MARKETING]: [PROFESSIONAL, ENTERPRISE]
      }
    })
  }],
  [WORKFLOWS_LIMIT_INCREASE]: [{
    fromVersion: 1,
    isRestricted: (state, {
      productConfigurations
    }) => isProductLevelInAnyProductLines(state, {
      productConfigurations,
      allowedProductLevels: [PROFESSIONAL, ENTERPRISE]
    })
  }],
  [USERS_LIMIT_INCREASE]: [{
    fromVersion: 1,
    isRestricted: (state, {
      productConfigurations
    }) => isProductLevelInAnyProductLines(state, {
      productConfigurations,
      allowedProductLevels: [PROFESSIONAL, ENTERPRISE]
    }) || isUngatedForSeatsSelector(state)
  }],
  [CUSTOM_OBJECTS_LIMIT_INCREASE]: [{
    fromVersion: 1,
    isRestricted: (state, {
      productConfigurations
    }) => isRestrictedProductLinesAndLevels(state, {
      productConfigurations,
      allowedProductLinesAndLevels: {
        [MARKETING]: [ENTERPRISE],
        [SALES]: [ENTERPRISE],
        [SERVICE]: [ENTERPRISE],
        [CMS]: [ENTERPRISE]
      }
    })
  }]
};
export const getAddonRestrictionDetails = ({
  apiName,
  versionNumber
}) => {
  if (!(apiName in AddOnsRestrictionMap)) {
    return null;
  }

  const restrictionVersions = AddOnsRestrictionMap[apiName];
  return restrictionVersions.find(({
    fromVersion,
    toVersion
  }) => versionNumber >= fromVersion && (!toVersion || versionNumber <= toVersion));
};